// urlNavigation — SPA URL helpers (path-style routing).
//
// 2026-05 改訂: ルーム URL を query `?room=<uuid>` から path `/<uuid>` に切替.
// 2026-05 追加改訂: view 系も query から path 化 (`?view=login|admin|create`
// → `/login` / `/admin` / `/create`). magic link 関連 (`/auth/verify?token=...`
// / `?action=create|verify` / `?name=xxx`) は **そのまま** — token は識別子
// だが dispatcher (`buildMagicLinkUrl`) 互換のため query 維持、action は
// 一時的な splash 遷移指示で bookmark しない、name は表示用 input data.
//
// 本モジュールは画面遷移の薄い helper を集約する. 設計方針:
//   - `parseRoomFromPathname` は **pure function** で testable.
//   - navigate 系は `window.history.pushState` + 同期 `popstate` 発火を介して
//     RootSwitch の useUrlState() フックを再評価させる. HomePage / LoginPage /
//     CreateRoomPage / RoomSettingsModal 等で同じパターンが散らばっていた
//     `setUrlSearch` を 1 箇所に集約する.
//   - `location.assign` 系の hard reload (= SessionProvider transport を
//     確実に作り直したい局面) は別関数 (`hardReloadToHome` 等) として分離する.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuidV7Like = (s: string): boolean => UUID_RE.test(s);

/**
 * Parse `window.location.pathname` → room UUID (lowercased) or null.
 *
 * - `/` または空文字 → null (= HomePage / view 経由 routing にフォールバック)
 * - `/<uuid>` → 該当 UUID (= ReJoinScreen に投入)
 * - `/auth/verify` 等の複数セグメント → null (= caller が pathname 全体で判定)
 * - `/INVALID` 等の non-UUID 単一セグメント → null (= 将来 `/about` 等の予約 path
 *   を追加する余地)
 *
 * Pure — no DOM access. tested in urlNavigation.test.ts.
 */
export const parseRoomFromPathname = (pathname: string): string | null => {
  const trimmed = pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  if (trimmed.length === 0) return null;
  // `/auth/verify` のような複数セグメントは room ではない (caller で別判定).
  if (trimmed.includes('/')) return null;
  return isUuidV7Like(trimmed) ? trimmed.toLowerCase() : null;
};

/* -----------------------------------------------------------------------------
 * Soft navigation (pushState + popstate dispatch)
 *
 * RootSwitch の useUrlState() が popstate を購読しているので、pushState 単体
 * (= ブラウザ操作以外) では再評価が走らない. そのため pushState 直後に同期で
 * PopStateEvent を発火させる "synthetic popstate" パターンを使う.
 * --------------------------------------------------------------------------- */

/**
 * Navigate to a room page (pathname `/<uuid>`).
 *
 * `query.name` を渡すと `?name=<displayName>` を URL に残す (= ReJoinScreen の
 * auto-join 用). それ以外の全 query (`view` / `action` / `token` 等) は消える.
 */
export const navigateToRoom = (roomId: string, query?: { readonly name?: string }): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.pathname = `/${roomId}`;
  url.search = '';
  if (query?.name !== undefined && query.name.length > 0) {
    url.searchParams.set('name', query.name);
  }
  window.history.pushState({}, '', url.toString());
  window.dispatchEvent(new PopStateEvent('popstate'));
};

/**
 * `replaceState` 版 — `?action=create` splash や magic link 着地等、splash 画面
 * の URL を履歴に残したくない局面で使う.
 */
export const replaceUrlToRoom = (roomId: string): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.pathname = `/${roomId}`;
  url.search = '';
  window.history.replaceState({}, '', url.toString());
  window.dispatchEvent(new PopStateEvent('popstate'));
};

/**
 * Navigate to HomePage (= pathname `/` + 全 query クリア).
 */
export const navigateToHome = (): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.pathname = '/';
  url.search = '';
  window.history.pushState({}, '', url.toString());
  window.dispatchEvent(new PopStateEvent('popstate'));
};

/**
 * View 系 page (`/login` / `/admin` / `/create`) への soft navigate.
 * room path は消す (= 「room から view へ移動」のセマンティクス).
 *
 * `extra.token` は magic link 等で view と一緒に query に残したい局面用.
 */
export type ViewName = 'create' | 'admin' | 'login';

/** Reserved view path → page name (App.tsx readUrlState で参照). */
export const VIEW_PATHS: Readonly<Record<ViewName, string>> = {
  create: '/create',
  admin: '/admin',
  login: '/login',
};

export const navigateToView = (view: ViewName, extra?: { readonly token?: string }): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.pathname = VIEW_PATHS[view];
  url.search = '';
  if (extra?.token !== undefined && extra.token.length > 0) {
    url.searchParams.set('token', extra.token);
  }
  window.history.pushState({}, '', url.toString());
  window.dispatchEvent(new PopStateEvent('popstate'));
};

/**
 * pathname から view 名を逆引きする pure helper. `/login` / `/admin` /
 * `/create` 以外は null. 末尾スラッシュは許容 (= `/login/` も valid).
 */
export const parseViewFromPathname = (pathname: string): ViewName | null => {
  const trimmed = pathname.replace(/\/+$/, '');
  for (const [name, path] of Object.entries(VIEW_PATHS) as Array<[ViewName, string]>) {
    if (trimmed === path) return name;
  }
  return null;
};

/* -----------------------------------------------------------------------------
 * Hard reload variants — for situations where SessionProvider / WSClient state
 * must be torn down (`'left'` phase, KickedScreen ホーム遷移, AdminPage
 * ログアウト等).
 * --------------------------------------------------------------------------- */

/**
 * Hard navigate to HomePage (= `location.assign('/')`). pathname `/` + search
 * 全クリアの URL に飛ばし、ブラウザに full reload させる.
 */
export const hardReloadToHome = (): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.pathname = '/';
  url.search = '';
  window.location.assign(url.toString());
};

/**
 * 現在の URL のまま full reload する (`location.reload()`).
 *
 * 用途 (2026-07-25 ユーザ指摘): 接続断 (= 自動再接続の打ち切り) 後の
 * ErrorScreen CTA。切断は一時的な事象 (Wi-Fi 切替 / スリープ復帰 / サーバ
 * 再起動) が大半で **部屋は生きている**ため、`hardReloadToHome` で `/` に
 * 飛ばすと「まだ描ける部屋を捨てさせる」ことになる。現 URL (= `/<uuid>`)
 * のまま読み直せば入室画面経由で復帰できる。部屋が本当に消えていた場合も
 * `room-not-found` 画面 (= そこに「新規ルームを作成」導線がある) に落ちる
 * ので、作成への道が失われるわけではない (1 手増えるだけ)。
 */
export const hardReloadCurrent = (): void => {
  if (typeof window === 'undefined') return;
  window.location.reload();
};

/**
 * Hard navigate to a room page (`location.assign('/<uuid>')`). KickedScreen の
 * 「再入室」のように WSClient を完全に作り直したい局面で使う.
 */
export const hardReloadToRoom = (roomId: string): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.pathname = `/${roomId}`;
  url.search = '';
  window.location.assign(url.toString());
};

/** 招待リンクの流入識別子 (`?ref=invite`)。`room.join.referrer` / session.start.referrer に入る。 */
export const INVITE_REF = 'invite';

/**
 * 「招待」ボタン用の部屋 URL を部屋 ID から組む (2026-10-01)。`window.location.href` を
 * そのまま使うと `?name=` / `?layout=` 等のクエリや hash が混ざるため、`replaceUrlToRoom`
 * と同じ正規形 (`<origin>/<roomId>`、query・hash なし) を origin + roomId から作る。
 * あいことばは URL に含めない (別に伝える)。
 *
 * 流入計測: `?ref=invite` を付ける。入室画面 (`/<roomId>?ref=invite`) を開いた時点で
 * SessionProvider が `readReferrerFromCurrentUrl` で読み取って `room.join.referrer` に載せる
 * (入室後に `replaceUrlToRoom` でクエリが消えても mount 時に保持済み)。
 */
export const buildRoomInviteUrl = (origin: string, roomId: string): string =>
  `${origin.replace(/\/+$/, '')}/${roomId}?ref=${INVITE_REF}`;
